import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import LoadError from "@/components/LoadError";
import dayjs from "dayjs";
import { Bell, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AppHeader } from "@/components/AppHeader";
import { useAuth } from "@/lib/auth";

const ROLE_HOME = { worker: "/crew", manager: "/foreman", admin: "/admin" };

export default function NotificationsPage() {
    const [rows, setRows] = useState([]);
    const navigate = useNavigate();
    const { employee } = useAuth();
    const home = ROLE_HOME[employee?.role] || "/";

    const [err, setErr] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const load = () => {
        setErr(null);
        return api.get("/notifications")
            .then((r) => { setRows(r.data); setLoaded(true); })
            .catch((e) => setErr(e?.response?.data?.detail || "Couldn’t load notifications."));
    };
    useEffect(() => { load(); }, []);

    // Where tapping a notification takes you, by kind. Anything unmapped just marks read.
    const destinationFor = (n) => {
        if (n.kind === "reminder" || n.kind === "approved") return home;
        if (n.kind === "safety") return `${home}/safety`;
        if (n.kind === "announcement") return `${home}/announcements`;
        return null;
    };

    const open = async (n) => {
        if (!n.read) {
            try { await api.post(`/notifications/${n.notification_id}/read`); } catch (e) { /* non-blocking */ }
        }
        const dest = destinationFor(n);
        if (!dest) { await load(); return; }
        navigate(dest);
    };

    if (err && !loaded) return <LoadError message={err} onRetry={load} testId="notifications-error" />;

    return (
        <div className="min-h-screen paper-bg" data-testid="notifications-page">
            <AppHeader />
            <div className="max-w-3xl mx-auto px-4 py-6 space-y-3 pb-12">
                <h2 className="font-bold text-2xl flex items-center gap-2"><Bell className="h-5 w-5 text-brand-navy" /> Notifications</h2>
                <ul className="space-y-2" data-testid="notifications-list">
                    {rows.length === 0 && <li className="text-sm text-brand-charcoal/60">Nothing yet. We'll ping you when there's news.</li>}
                    {rows.map((n) => {
                        const hasDest = !!destinationFor(n);
                        return (
                            <li key={n.notification_id}
                                onClick={() => open(n)}
                                data-testid={`notif-${n.notification_id}`}
                                className={`p-4 rounded-2xl border shadow-card cursor-pointer ${n.read ? "bg-white border-brand-charcoal/10" : "bg-brand-gold/20 border-brand-navy/30"}`}>
                                <div className="flex justify-between text-xs uppercase tracking-widest text-brand-navy">
                                    <span>{n.kind}</span>
                                    <span className="text-brand-charcoal/50 normal-case tracking-normal">{dayjs(n.created_at).format("MMM D · h:mm A")}</span>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="font-bold mt-1">{n.title}</div>
                                        <div className="text-sm text-brand-charcoal/80 mt-0.5 whitespace-pre-line">{n.body}</div>
                                    </div>
                                    {hasDest && <ChevronRight className="h-5 w-5 text-brand-navy shrink-0" data-testid={`notif-open-${n.notification_id}`} />}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}
